import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Chart card (Paper → App → Foundations, "Charts · Card pattern · Oct 2026"):
 * one metric per card, this period against the last. White, 12px radius,
 * hairline ring, no shadow, 20px padding, 20px from header to chart.
 *
 * Header: 14/500 title (26px row, so a segmented control fits on the right),
 * 28/600 tabular number, an ink 13/500 delta with a muted "vs <range>", and a
 * legend of 2px rings bottom-aligned with the delta row.
 */

export interface ChartLegendItem {
  label: string
  /** CSS color of the ring */
  color: string
}

export function ChartLegendRing({ color, label }: ChartLegendItem) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="size-2 shrink-0 rounded-full border-2" style={{ borderColor: color }} aria-hidden />
      <span className="whitespace-nowrap text-xs leading-4 text-muted-foreground">{label}</span>
    </span>
  )
}

export interface ChartCardProps {
  title: string
  /** Right of the title: a segmented control or a muted meta line */
  headerAction?: ReactNode
  /** The big number, already formatted */
  value?: ReactNode
  /** "+15.0%" (ink); omit when there is no honest comparison */
  delta?: string | null
  /** "vs Sep 12 to Sep 18" with a delta, or the current range without one */
  comparison?: string | null
  legend?: ChartLegendItem[]
  /** Title, legend and dates stay real; number and plot are flat blocks */
  loading?: boolean
  className?: string
  children: ReactNode
}

export function ChartCard({
  title,
  headerAction,
  value,
  delta,
  comparison,
  legend = [],
  loading = false,
  className,
  children,
}: ChartCardProps) {
  return (
    <section
      className={cn(
        'flex min-w-0 flex-col gap-5 rounded-xl bg-card p-5 ring-1 ring-border',
        className,
      )}
      aria-busy={loading || undefined}
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex h-[26px] shrink-0 items-center justify-between gap-3">
          <h3 className="truncate text-sm font-medium leading-5 text-foreground">{title}</h3>
          {headerAction}
        </div>
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
          {loading ? (
            <div className="flex flex-col gap-2 pt-0.5">
              <Skeleton className="h-7 w-[104px] rounded-md" />
              <Skeleton className="h-3 w-[150px]" />
            </div>
          ) : (
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-[28px] font-semibold leading-8 tracking-[-0.02em] text-foreground tabular-nums">
                {value}
              </span>
              {(delta || comparison) && (
                <span className="flex items-baseline gap-1.5 text-[13px] leading-4">
                  {delta && <span className="font-medium text-foreground tabular-nums">{delta}</span>}
                  {comparison && <span className="text-muted-foreground">{comparison}</span>}
                </span>
              )}
            </div>
          )}
          {legend.length > 0 && (
            <div className="flex items-center gap-3.5 pb-0.5">
              {legend.map((item) => (
                <ChartLegendRing key={item.label} {...item} />
              ))}
            </div>
          )}
        </div>
      </div>
      {children}
    </section>
  )
}

/** Quiet empty state inside the plot: what is missing, why, one action. */
export function ChartEmpty({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="absolute inset-x-0 top-0 flex h-[172px] flex-col items-center justify-center gap-1 px-4 text-center">
      <p className="text-[13px] font-medium leading-4 text-foreground">{title}</p>
      <p className="text-[13px] leading-4 text-muted-foreground">{body}</p>
      {action && <div className="mt-2.5">{action}</div>}
    </div>
  )
}
